<template>
  <div class="interaction-data-card">
    <v-list class="branded-background">
      <v-list-tile avatar>
        <v-list-tile-avatar>
          <v-icon color="white">{{interactionIconMap[interaction.interaction_type]}}</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title class="row-space modal-title">
            <span class="text-white">{{interactionTypeMap[interaction.interaction_type]}}</span>
            <span class="row-center text-white"><v-icon class="time-icon">access_time</v-icon>{{interaction.display_in_time}}</span>
          </v-list-tile-title>
        </v-list-tile-content>
        <v-icon @click="slideOut({toggle: false, view: null, data: null})" color="white">close</v-icon>
      </v-list-tile>
    </v-list>
    
    <!-- update to use interaction review-->
    <v-layout
      class="interaction-data-content"
    >
      <v-flex xs6>
        <v-layout>
          <PlaypositInteraction
            :bulb-id="parseInt($route.query.bulb_id, 10)"
            :section-id="parseInt(sectionId, 10)"
            :interaction-id="parseInt(interaction.interaction_id, 10)"
            :token="instructorData.token"
            user-type="teacher"
            discussion-role="observer"
            perspective="displayMaximum"
          />
        </v-layout>
      </v-flex>
      <v-flex xs6>
        <v-layout
          column
          justify-center
          align-center
        >
          <div
            v-if="interaction.interaction_type === 'discussion_q'"
            class="text-xs-center full-width mb-4"
          >
            <v-btn
              block
              outline
              @click="downloadDiscussions"
              label="download dicussions"
              aria-label="Download Discussions as Text File"
              color="grey darken-1"
            >
              <v-icon left>file_download</v-icon>
              Export Discussions Attempts
            </v-btn>
          </div>
          <div v-if="chartAvailable">
            <div class="bar">
              <BarChart
                :key="barChartKey"
                :chart-data="barChart.data"
                :options="barChart.options"
              ></BarChart>
            </div>
            <div
              v-if="showPieChart"
              class="pie"
            >
              <PieChart
                :key="pieChartKey"
                :chart-data="pieChart.data"
                :options="pieChart.options"
              ></PieChart>
            </div>
          </div>
          <div
            v-if="!chartAvailable && interactionGraphSupported"
            class="text-xs-center"
          >
            <v-icon large>{{chartMessage === "Error Loading Chart" ? 'error' : 'bar_chart'}}</v-icon>
            <div class="title">{{chartMessage}}</div>
            <v-progress-circular
              v-if="chartMessage === 'Fetching Chart Data'"
              indeterminate
              color="primary"
              :size="50"
              :width="5"
              class="mt-4"
            ></v-progress-circular>
          </div>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
/* global _ */
/* global moment */
/* global Blob */
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import _ from 'lodash';
import BarChart from '../graphs/BarChart';
import PieChart from '../graphs/PieChart';
import FileSaver from 'file-saver';

export default {
  name: 'InteractionData',
  components: {
    BarChart, 
    PieChart,
  },
  props: [ 
    'displayTime',
    'html',
    'interaction',
    'sectionId',
  ], 
  data() {
    return {
      chartAvailable: null,
      chartMessage: 'Fetching Chart Data',
      discussionAttempts: [],
      barChart: {
        data: {},
        options: {
          legend: {
            display: false,
          }, 
          title: {
            display: true, 
            position: 'top',
            text: `Answer Summary`,
          }
        },
      },
      pieChart: {
        data: {},
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'top'
          },
          title: {
            display: true, 
            position: 'top',
            text: `Percentage Correct`,
          },
          plugins: {
            datalabels: {
              formatter: (value, ctx) => {
                let sum = 0;
                let dataArr = ctx.chart.data.datasets[0].data;
                dataArr.map(data => {
                    sum += data;
                });
                let percentage = (value*100 / sum).toFixed(0);
                if (percentage > 0) return `${percentage}%`
                return '';
              },
              color: '#fff',
            }
          },
        },
      },
      interactionGraphSupported: ![
        'short_answer',
        'free_response',
        'discussion_q',
        'pause_q',
        'iframe_q',
        'file_viewer_q'
      ].includes(this.interaction.interaction_type)
    } // end return 
  }, // end data
  computed: {
    ...mapGetters([
      'interactionIconMap',
      'interactionTypeMap',
      'instructorData',
      'learnerData',
      'simpleDash'
    ]),
    ...mapGetters('instructors', [
      'instructorAjaxing',  
      'activeViewInstructor',
    ]),
    barChartKey() {
      return `bar-${this.interaction.interaction_id}`;
    },
    pieChartKey() {
      return `pie-${this.interaction.interaction_id}`;
    },
    showPieChart() {
      return ['multiple_choice', 'check_all_apply'].includes(this.interaction.interaction_type);
    }
  },
  methods: {
    ...mapMutations([
      'slideOut',
    ]),
    ...mapActions('instructors', [
      'getChartData',
    ]),
    async getDiscussion() {
      try {
        // refresh discussion data
        if (this.activeViewInstructor === 'Interactions') {
          const da = await this.$store.dispatch('instructors/getDiscussionData', {
                              interactionId: this.interaction.interaction_id,
                              sectionId: this.sectionId,
                              token: this.instructorData.token
                            });
          this.discussionAttempts = da.data;
        }
      } catch(e) {
        console.log(e, 'Error getting discussionAttempts');
      }
    },
    //helper functions to format data
    fromNow(time) {
       //helper for discussions to format time
      return moment(time).fromNow();
    },
    formatedDuration(time) {
      //helper for discussions to format time
      const sec_num = parseInt(time, 10); // don't forget the second param
      let hours   = Math.floor(sec_num / 3600);
      let minutes = Math.floor((sec_num - (hours * 3600)) / 60);
      let seconds = sec_num - (hours * 3600) - (minutes * 60);
      if (!hours) hours = 0;
      if (!minutes) minutes = 0;
      if (!seconds) seconds = 0;
      return (hours >= 1 ? hours+":" : "") + (minutes < 10 ? "0"+minutes : minutes) + ":" + (seconds < 10 ? "0"+seconds : seconds);
    },
    downloadDiscussions() {
      if (this.discussionAttempts.length > 0) {
        let html = '';
        this.discussionAttempts.forEach((c, i) => {
          c.text = c.text.replace(/\<(?!img|audio).*?\>\%09%u2022/g, "");
          if (c.text.match(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g)) {
            c.text = c.text.replace(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g, '$3');
            c.text = c.text.replace(/\↵*/g, '').split("\n");
            c.text = c.text.slice(1, (c.text.length - 1));
            html += c.name +` - ${this.fromNow(c.created)}` + '\r\n' +`(${this.formatedDuration(c.displayTime)}) - | ` + c.text.join('\r\n          |\r\n          | ') + '\r\n\r\n';
          } else {
            html += c.name +` - ${this.fromNow(c.created)}` + '\r\n' +`(${this.formatedDuration(c.displayTime)}) - ` + c.text + '\r\n';
            html += c.replies.length ? '\\' : '\r\n';
          }
          if (c.replies.length > 0) {
            c.replies.forEach((r, i) => {
              r.text = r.text.replace(/\<(?!img|audio).*?\>\%09%u2022/g, "");
              if (r.text.match(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g)) {
                r.text = r.text.replace(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g, '$3');
                r.text = r.text.replace(/\↵*/g, '').split("\n");
                r.text = r.text.slice(1, (r.text.length - 1));
                html += '\r\n  |--'+ r.name +` - ${this.fromNow(r.created)}` + '\r\n'+'  |  '+`(${this.formatedDuration(r.displayTime)}) - | ` + r.text.join('\r\n  |            |\r\n  |            | ');
                html += (c.replies.length-1) === i ? '\r\n\r\n' : '\r\n  |\r\n  |'; 
              } else {
                html += '\r\n  |--'+ r.name +` - ${this.fromNow(r.created)}` + '\r\n'+'  |  '+`(${this.formatedDuration(r.displayTime)}) - ` + r.text; 
                html +=  (c.replies.length-1) === i ? '\r\n\r\n' : '\r\n'+'  |'+ '\r\n'+'  |';
              }
            });
          }
        });
        const blob = new Blob([html], {type: 'text/plain;charset=utf-8'});
        FileSaver.saveAs(blob, `playposit-discussions-${this.interaction.interaction_id}.txt`);
      }
    },
  },
  async mounted() {
    if (this.interaction.interaction_type === 'discussion_q') {
      this.getDiscussion();
    }
    if (!this.interactionGraphSupported) {
      this.chartAvailable = false;
      this.chartMessage = `${this.interactionTypeMap[this.interaction.interaction_type]} Chart Coming Soon`;
    } else {
      const { 
        optionCount, 
        correctCount,
        message
      } = await this.getChartData({
        interactionId: this.interaction.interaction_id, 
        sectionId: this.sectionId,
      });
      if (message) {
        this.chartAvailable = false;
        this.chartMessage = 'Error Loading Chart';
      } else {
        this.chartAvailable = true;
        this.barChart.data = optionCount;
        this.pieChart.data = correctCount;
      }
    }
  },
};
</script>

<style scoped>

  .interaction-data-card {
    height: 100%;
  }
  .interaction-data-content {
    height: calc(100% - 56px);
    overflow-y: auto;
    overflow-x: none;
    padding: 2em;
  }
  .interaction-data {
    float: left;
    width: 50%;
    overflow:;
  }
  .bar div:first-child { 
    position: relative;
    height: 300px;
  }
  .pie div:first-child { 
    position: relative;
    height: 300px;
  }
</style>
